import {
  IFontPickerStyleProps,
  IFontPickerTextFieldStyleProps,
} from "modern-font-picker";

import {
  concatStyleSets,
  getHighContrastNoAdjustStyle,
  getRTL,
  HighContrastSelector,
  IStyleFunction,
  ITheme,
  memoizeFunction,
} from "@fluentui/react";

import {
  baseFabricFocusAfterSelector,
  baseFabricFocusVisibleSelector,
  commonHighContrastDisabledStyles,
  commonHighContrastFocusedDisabledStyles,
  commonHighContrastHoveredDisabledStyles,
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineHoverFocus,
  controlStylingConstants,
  getActionControlSemanticColorsWithTheme,
  getCaretDownButtonStyles,
  getInnerBorderStyles,
  getModernUxExperimentPreference,
  getStandardControlSemanticColorsWithTheme,
  modernUxControlsBorderRadius,
  StandardControlSemanticColors,
} from "@1js/acui-common";
import { AppLabelStyles } from "@1js/acui-label";

import {
  AppFontPickerActionButtonStyleProps,
  AppFontPickerActionButtonStyles,
  AppFontPickerDropDownStyles,
  AppFontPickerItemStyleProps,
  AppFontPickerStyles,
  AppFontPickerTextFieldStyleProps,
  AppFontPickerTextFieldStyles,
} from "./AppFontPicker.Props";

const chevronButtonHighContrastInvertedStyling = {
  backgroundColor: "Highlight",
  selectors: {
    "svg > path.OfficeIconColors_HighContrast": {
      fill: "HighlightText !important",
    },
  },
  ...getHighContrastNoAdjustStyle(),
};

const itemStyles = memoizeFunction(
  (theme: ITheme) => (props: AppFontPickerItemStyleProps) => {
    const {
      disabled,
      isSelected,
      isPrimaryButtonHover,
      isHover,
      isPressed,
      submenuOpen,
    } = props;
    const colors = getStandardControlSemanticColorsWithTheme(theme);
    return {
      root: [
        {
          [baseFabricFocusAfterSelector]: getInnerBorderStyles({
            width: "2px",
            color: colors.focusedBorderColor,
          }),
        },
        isSelected &&
          !disabled &&
          !isPrimaryButtonHover &&
          !submenuOpen && {
            backgroundColor: colors.selectedBackgroundColor,
            ":after": getInnerBorderStyles({
              width: "1px",
              color: colors.selectedBorderColor,
            }),
            [HighContrastSelector]: chevronButtonHighContrastInvertedStyling,
          },
        isSelected &&
          !disabled &&
          submenuOpen && {
            [HighContrastSelector]: chevronButtonHighContrastInvertedStyling,
          },
        !disabled &&
          !isPressed &&
          isHover && {
            backgroundColor: colors.hoverBackgroundColor,
            ":after": getInnerBorderStyles({
              width: "0px",
              color: "transparent",
            }),
          },
        !disabled && {
          [HighContrastSelector]: [
            { ":focus": chevronButtonHighContrastInvertedStyling },
          ],
        },
      ],
      icon: [
        !disabled && {
          selectors: {
            [HighContrastSelector]: {
              selectors: {
                ":hover": {
                  selectors: chevronButtonHighContrastInvertedStyling.selectors,
                }, // The PinActionButton already handles setting the background on hover
                ":focus": chevronButtonHighContrastInvertedStyling,
              },
            },
          },
        },
        isPrimaryButtonHover &&
          !disabled && {
            selectors: {
              [HighContrastSelector]: chevronButtonHighContrastInvertedStyling,
            },
          },
        {
          selectors: {
            [`${baseFabricFocusVisibleSelector} & [role="button"]:focus:after`]:
              {
                outlineWidth: "2px",
                outlineOffset: "-1px",
              },
          },
        },
      ],
      menuButton: [
        !disabled && {
          selectors: {
            [HighContrastSelector]: {
              ...((isPrimaryButtonHover || submenuOpen) &&
                chevronButtonHighContrastInvertedStyling),
              ":focus": chevronButtonHighContrastInvertedStyling,
              ":hover": chevronButtonHighContrastInvertedStyling,
            },
          },
        },
      ],
    };
  }
);

const dropDownStyles = (
  theme: ITheme
): Partial<AppFontPickerDropDownStyles> => {
  const actionControlSemanticColors =
    getActionControlSemanticColorsWithTheme(theme);
  return {
    subComponentStyles: {
      separatorStyles: {},
      itemStyles: itemStyles(theme),
      headerStyles: {
        expand: [
          {
            fill: actionControlSemanticColors.textColor,
            [baseFabricFocusAfterSelector]: {
              outlineWidth: "2px",
              outlineOffset: "-1px",
            },
            selectors: {
              [HighContrastSelector]: {
                selectors: {
                  ":active": chevronButtonHighContrastInvertedStyling,
                  ":focus": chevronButtonHighContrastInvertedStyling,
                  ":hover": chevronButtonHighContrastInvertedStyling,
                  "svg > path.OfficeIconColors_HighContrast": {
                    fill: "ButtonText !important",
                  },
                },
              },
            },
          },
        ],
      },
    },
  };
};

const actionButtonStyles: IStyleFunction<
  AppFontPickerActionButtonStyleProps,
  AppFontPickerActionButtonStyles
> = (props) => {
  const { disabled } = props;
  return {
    root: [
      !disabled && {
        selectors: {
          [HighContrastSelector]: {
            selectors: {
              ":focus": chevronButtonHighContrastInvertedStyling,
            },
          },
        },
      },
    ],
  };
};

function getBorderRadius(
  theme: ITheme,
  connectToNextElement?: boolean
): string | number {
  // If FluentUI flight is not ON, we want square corners.
  if (!getModernUxExperimentPreference()) {
    return 0;
  }

  // If the control is not connected to the next element, we want all 4 rounded corner
  if (!connectToNextElement) {
    return modernUxControlsBorderRadius;
  }

  // When Font Picker stands next to the Font Size combobox, we want only 2 rounded corners.
  // Which side it is depends on RTL direction.
  return getRTL(theme)
    ? `0 ${modernUxControlsBorderRadius}px ${modernUxControlsBorderRadius}px 0`
    : `${modernUxControlsBorderRadius}px 0 0 ${modernUxControlsBorderRadius}px`;
}

function getTextFieldStyles(
  props: AppFontPickerTextFieldStyleProps,
  connectToNextElement?: boolean
): Partial<AppFontPickerTextFieldStyles> {
  const { isActive, disabled, theme } = props;
  const standardControlSemanticColors: StandardControlSemanticColors =
    getStandardControlSemanticColorsWithTheme(theme);
  const { comboBoxHeight, comboBoxVerticalPadding } = controlStylingConstants;

  return {
    root: [
      {
        height: comboBoxHeight,
        boxSizing: "border-box",
        alignItems: "center",
        overflow: "hidden",
        borderColor: isActive
          ? standardControlSemanticColors.inputFocusBorderColor
          : standardControlSemanticColors.inputBorderColor,
        zIndex: isActive ? 1 : undefined,
        backgroundColor: disabled
          ? standardControlSemanticColors.disabledBackgroundColor
          : standardControlSemanticColors.inputBackgroundColor,
        whiteSpace: "nowrap",
        padding: 0,
        borderRadius: getBorderRadius(theme, connectToNextElement),
        selectors: {
          [HighContrastSelector]: [
            {
              borderColor: "ButtonText",
              ...getHighContrastNoAdjustStyle(),
            },
            !disabled && {
              selectors: {
                ":hover": [
                  commonHighContrastHoverFocusStyles,
                  chevronButtonHighContrastInvertedStyling,
                ],
              },
            },
            commonHighContrastOutlineHoverFocus,
          ],
        },
      },
      isActive &&
        !disabled && [
          commonHighContrastHoverFocusStyles,
          {
            selectors: {
              [HighContrastSelector]: chevronButtonHighContrastInvertedStyling,
            },
          },
        ],
      disabled && commonHighContrastDisabledStyles,
      disabled &&
        isActive && [
          commonHighContrastFocusedDisabledStyles,
          commonHighContrastHoveredDisabledStyles,
        ],
    ],
    textInput: [
      {
        borderRadius: 0,
        color: disabled
          ? standardControlSemanticColors.inputTextDisabledColor
          : standardControlSemanticColors.inputTextColor,
        height: comboBoxHeight - comboBoxVerticalPadding,
        backgroundColor: "transparent",
        lineHeight: comboBoxHeight - comboBoxVerticalPadding,
        padding: "0px 0px 0px 4px",
        ["::selection"]: {
          backgroundColor:
            standardControlSemanticColors.inputSelectionBackgroundColor,
          color: standardControlSemanticColors.inputSelectionTextColor,
          [HighContrastSelector]: {
            color: "HighlightText",
            backgroundColor: "Highlight",
            ...getHighContrastNoAdjustStyle(),
          },
        },
      },
      disabled && {
        [HighContrastSelector]: {
          color: "GrayText",
          backgroundColor: "ButtonFace",
          ...getHighContrastNoAdjustStyle(),
          "::selection": {
            color: "GrayText !important",
            backgroundColor: "Highlight",
            ...getHighContrastNoAdjustStyle(),
          },
        },
      },
    ],
    subComponentStyles: {
      buttonStyles: getCaretDownButtonStyles(theme, {
        root: {
          top: 0,
        },
        icon: {
          fontSize: 12,
        },
      }),
    },
  };
}

export const getStyleFunction: (
  customStyles?: Partial<AppFontPickerStyles>,
  connectToNextElement?: boolean
) => IStyleFunction<IFontPickerStyleProps, AppFontPickerStyles> =
  memoizeFunction(
    (customStyles, connectToNextElement) => (props: IFontPickerStyleProps) => {
      const { theme } = props;
      const { controlHeight, comboBoxBorderWidth, comboBoxRightMargin } =
        controlStylingConstants;

      return concatStyleSets(
        {
          root: {
            height: controlHeight,
            marginRight: comboBoxRightMargin,
            borderWidth: comboBoxBorderWidth,
          },
          subComponentStyles: {
            actionButtonStyles,
            dropDownStyles: dropDownStyles(theme),
            textFieldStyles: (textFieldProps: IFontPickerTextFieldStyleProps) =>
              getTextFieldStyles(textFieldProps, connectToNextElement),
          },
        },
        customStyles
      );
    }
  );

export const labelContainerStyles: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
};

export const getLabelStyle = memoizeFunction(
  (
    theme: ITheme,
    disabled?: boolean,
    customStyles?: Partial<AppLabelStyles>
  ): AppLabelStyles => {
    const semanticColors = getStandardControlSemanticColorsWithTheme(theme);

    const defaultStyles = {
      label: disabled && [
        {
          color: semanticColors.inputTextDisabledColor,
          cursor: "default",
        },
        commonHighContrastDisabledStyles,
      ],
    };

    return concatStyleSets(defaultStyles, customStyles);
  }
);
